import { useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useTranslation } from '@govard/i18n-core/react';
import { useMeasuredTabs } from './useMeasuredTabs.js';

export interface EntityTab {
  key: string;
  label: string;
  count?: number;
  tooltip?: string;
  content: ReactNode;
}

export function EntityTabs({ items }: { items: EntityTab[] }): ReactElement {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const id = useId();
  const pendingFocus = useRef(false);
  const requested = params.get('tab');
  const key = requested === 'info' ? 'overview' : requested === 'activity' ? 'history' : requested;
  const active = items.find((item) => item.key === key) ?? items[0];
  const { bar, measures, visibleKeys } = useMeasuredTabs(items, active?.key);
  const label = (item: EntityTab): string =>
    `${item.label}${item.count === undefined ? '' : ` (${item.count})`}`;

  useEffect(() => {
    if (!pendingFocus.current || !active || !visibleKeys.includes(active.key)) return;
    document.getElementById(`${id}-${active.key}`)?.focus();
    pendingFocus.current = false;
  }, [active, id, visibleKeys]);
  const change = (next: string): void => {
    const search = new URLSearchParams(params);
    if (next === 'overview') search.delete('tab');
    else search.set('tab', next);
    setParams(search);
  };
  const visible = items.filter((item) => visibleKeys.includes(item.key));
  const overflow = items.filter((item) => !visibleKeys.includes(item.key));
  return (
    <div className="entity-tabs entity-panel">
      <div className="entity-tabs-measure" ref={measures} aria-hidden="true">
        {items.map((item) => (
          <span
            className="entity-tab-measure"
            data-measure-tab
            key={item.key}
            style={{ fontWeight: active?.key === item.key ? 600 : undefined }}
          >
            {label(item)}
          </span>
        ))}
        {items.map((item, index) => (
          <Button
            data-measure-more
            key={item.key}
            type="text"
            icon={<DownOutlined />}
            iconPlacement="end"
            tabIndex={-1}
          >
            {t('crm.entity_detail.more_tabs')} ({index + 1})
          </Button>
        ))}
      </div>
      <div
        ref={bar}
        className="entity-tabs-wide"
        role="tablist"
        aria-label={t('crm.entity_detail.tabs')}
      >
        {visible.map((item) => (
          <Tooltip key={item.key} title={item.tooltip}>
            <button
              type="button"
              data-testid={`entity-tab-${item.key}`}
              role="tab"
              id={`${id}-${item.key}`}
              aria-selected={active?.key === item.key}
              aria-controls={`${id}-panel`}
              tabIndex={active?.key === item.key ? 0 : -1}
              onClick={() => change(item.key)}
              onKeyDown={(event) => {
                if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const index = items.indexOf(item);
                const next =
                  event.key === 'Home'
                    ? items[0]
                    : event.key === 'End'
                      ? items.at(-1)
                      : items[
                          (index + (event.key === 'ArrowRight' ? 1 : items.length - 1)) %
                            items.length
                        ];
                if (next) {
                  pendingFocus.current = true;
                  change(next.key);
                }
              }}
            >
              {label(item)}
            </button>
          </Tooltip>
        ))}
        {overflow.length ? (
          <Dropdown
            trigger={['click']}
            menu={{
              items: overflow.map((item) => ({
                key: item.key,
                label: label(item),
                onClick: () => {
                  pendingFocus.current = true;
                  change(item.key);
                },
              })),
            }}
          >
            <Button
              data-testid="entity-tabs-more"
              type="text"
              icon={<DownOutlined />}
              iconPlacement="end"
              aria-label={`${t('crm.entity_detail.more_tabs')} (${overflow.length})`}
            >
              {t('crm.entity_detail.more_tabs')} ({overflow.length})
            </Button>
          </Dropdown>
        ) : null}
      </div>
      <div
        role="tabpanel"
        id={`${id}-panel`}
        aria-labelledby={active ? `${id}-${active.key}` : undefined}
        className="entity-tab-content"
        key={active?.key}
      >
        {active?.content}
      </div>
    </div>
  );
}
